import React, { useState, useEffect } from 'react';

import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  lighten,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { FormTextField } from 'components/forms/FormikFields';
import { Formik, Form } from 'formik';

import showNotification from 'commons/helpers/showNotification';
import yup from 'commons/validations';
import ColorPicker from 'components/pickers/ColorPicker';
import CourtService from 'services/CourtService';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  paper: {
    borderRadius: '4px',
    padding: theme.spacing(2),
  },
  actionsRoot: {
    justifyContent: 'space-between',
  },
  tooltip: {
    fontSize: '12px',
    backgroundColor: 'rgba(78, 77, 77, 0.9)',
  },
  permissionRadioLabelDescription: {
    fontSize: '13px',
  },
  checkWithPasswordLabel: { color: lighten(theme.palette.text.secondary, 0.2), fontSize: '13px' },
  divider1: { marginTop: '6px', marginBottom: '0px' },
  divider2: { marginTop: '0px', marginBottom: '0px' },
  divider3: { marginTop: '10px', marginBottom: '4px' },

  btnSelectRole: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    padding: '6px 0px 6px 0px',
    '&:hover': {
      backgroundColor: 'rgba(128, 10, 192, 0.04)',
      borderBottom: '2px solid #263238',
      padding: '6px 0px 5px 0px',
    },
  },
  btnSelectRoleError: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderBottom: `2px solid ${theme.palette.error.main}`,
    padding: '6px 0px 6px 0px',
    '&:hover': {
      backgroundColor: 'rgba(128, 10, 192, 0.04)',
    },
  },
  btnSelectRoleContainer: {
    marginLeft: '8px',
    marginBottom: '5px',
  },
  btnSelectRoleLabelValue: {
    whiteSspace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: '10px',
  },
  formLabel: { color: '#263238' },
  formLabelError: { color: theme.palette.error.main },
  labelPlaceholder: { color: 'rgb(112, 112, 112)', fontStyle: 'italic' },

  radioButton: {
    paddingLeft: '15px',
    paddingBottom: '10px !important',
  },
}));

const schema = yup.object().shape({
  name: yup.string().required().max(100),
  colorId: yup.string().nullable(),
});

const DEFAULT_INIT_VALUES = {
  name: '',
  colorId: null,
};

// COMPONENT
function CourtFormModal({ courtId, open, onClose, onSaveCallback }) {
  const { classes } = useStyles();
  const [initialValues, setInitialValues] = useState(DEFAULT_INIT_VALUES);

  useEffect(() => {
    if (open) {
      if (courtId) {
        CourtService.getById(courtId).then((resp) => {
          setInitialValues({ ...resp, timestamp: new Date().getTime() });
        });
      } else {
        setInitialValues({
          ...DEFAULT_INIT_VALUES,
          timestamp: new Date().getTime(),
        });
      }
    }
  }, [open, courtId]);

  const myHandleSubmit = (values, actions) => {
    const submission = {
      ...values,
      colorId: values.colorId === -1 ? null : values.colorId,
    };

    CourtService.save(submission)
      .then((savedObj) => {
        if (!values.id) {
          showNotification('success', 'Criado com sucesso.');
          onSaveCallback(savedObj);
        } else {
          showNotification('success', 'Atualizado com sucesso.');
          onSaveCallback(savedObj);
        }
      })
      .catch(() => {
        actions.setSubmitting(false);
      });
  };

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="span">
          {initialValues && initialValues.id ? 'Atualizando Quadra' : 'Criando Quadra'}
        </Typography>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={myHandleSubmit}
      >
        {({ dirty, isSubmitting, values, setFieldValue, touched, errors }) => {
          return (
            <Form>
              <DialogContent dividers>
                <Box display="flex" flexDirection="row">
                  <FormTextField fullWidth type="text" label="Nome" name="name" />
                  <ColorPicker
                    showRomoveSelection
                    removeSelectionText="Manter minha cor da agenda"
                    label="Selecione a cor de identificação"
                    selectedColorId={values.colorId}
                    setSelectedColorId={(colorId) => {
                      setFieldValue('colorId', colorId);
                    }}
                  />
                </Box>
              </DialogContent>

              <DialogActions
                classes={{
                  root: classes.actionsRoot,
                }}
              >
                <Button variant="outlined" onClick={onClose}>
                  Fechar
                </Button>
                <Button variant="contained" type="submit" color="primary" disabled={!dirty || isSubmitting}>
                  Salvar
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export default CourtFormModal;
